import {initializeSupport, resetSupport} from '../Support/messenger';
import IDP_CONFIG from './idp-config';
import {tabSession} from './session';
import {SessionChangedError, type LoginCandidate, type SessionGeneration} from './tabSession';
import {changeEmailErrorMessage} from './changeEmailError';
import * as api from '@/rest/api';
import {confirmError, confirmSuccess} from '@/ui/Confirm';
import {persistenceGate} from './persistenceGate';
import {clearSignedOut} from './signedOutMarker';
import {setLoggingUser} from "@/lib/remoteLogging";
import {stopRecording} from '@/lib/sessionReplay';
import locals from '@/lib/localStorage';
import * as polling from '@/queues/polling';
import store from '@/store/store';
import * as MiscActions from '@/store/actions/miscActions';
import util from '@/lib/util';
import pWaitFor from 'p-wait-for';
import Alert from '@/lib/alert';
import {fetchFromServer} from "@/queues/processQueue";
import {
    getCurrentFirm,
    getCurrentFirmId,
    getCurrentUser,
    getCurrentUserId,
} from "@/lib/picos/currentLogin";
import {uuid} from "@/@types/schema";
import User from "@/entities/user";
import * as idb from "@/lib/idbd";
import {socket} from "@/lib/picos/socket";
import {globalAbortSignal} from "@/lib/picos/abortController";

// Re-export role utilities for backward compatibility
import {
    LEGAL_ROLES, ALL_RELATED,
    getRoles, getMyRoles, hasRole, hasRoles, hasRoleId, hasLegalRole,
    isAdmin, isOwner, isUser, isInGroup,
    grantRole, grantRoleId, revokeRole, revokeRoleId
} from './roles';

const logging = locals.getBoolean(locals.AUTH_LOGGING, false);
const logFormat = "background: #fdebd0; padding: 2px; border-radius: 4px; color: black";

/**
 * Build a user-actionable login error message from an arbitrary error or
 * Result error. Empty messages and `Failed to fetch` get a friendly fallback
 * so the user is never shown a bare "Error Logging In" with no body.
 *
 * Used by `runPostLoginSequence` to populate `state.misc.authenticationError`,
 * which `Home.tsx` reads to render an inline error screen with Retry / Sign out
 * actions.
 */
/**
 * Build a user-actionable login error with a title that matches the failure.
 * Returns `{title, message}` for `state.misc.authenticationError`.
 */
function buildLoginError(err: any): {title: string; message: string} {
    const raw =
        (typeof err?.message === 'string' && err.message) ||
        (typeof err?.message?.clientMessage === 'string' && err.message.clientMessage) ||
        '';

    const status = typeof err?.status === 'number' ? err.status : 0;

    // Network-level failures — server down, DNS failure, CORS from a 502, etc.
    if (!raw || raw.includes('Failed to fetch')) {
        return {
            title: 'Server Unreachable',
            message: 'We could not reach the Outlaw Practice servers. Please check your internet connection and try again.',
        };
    }

    // HTTP 5xx from a server that responded but errored internally
    if (status >= 500) {
        return {
            title: 'Server Error',
            message: raw || 'The server encountered an unexpected error. Please try again in a moment.',
        };
    }

    // HTTP 4xx — likely auth/permission issue
    if (status >= 400) {
        return {
            title: 'Authentication Error',
            message: raw,
        };
    }

    // Anything else
    return {
        title: 'Sign-in error',
        message: raw,
    };
}

/**
 * A Zitadel sign-in the server refused. The person did sign in, so a 401 means the account is not
 * connected to its firm (no binding, typically right after the firm moves to the new sign-in) or is
 * no longer active (disabled or deleted), and a 403 means no access to the firm (OWA-1485). The
 * client cannot tell the 401 causes apart (`api.verifyLogin` replaces the body), so the message
 * names both. Other refusals keep the general message.
 */
export function buildZitadelRefusal(err: any): {title: string; message: string} {
    const status = typeof err?.status === 'number' ? err.status : 0;
    if (status === 401) {
        return {
            title: 'Account Not Connected',
            message: 'You signed in, but Outlaw Practice could not connect this account to your firm, ' +
                'or the account is no longer active. If your firm has just moved to the new sign-in page, ' +
                'your account may not be set up yet. Ask your firm\'s administrator or Outlaw Support, ' +
                'then choose Retry.',
        };
    }
    if (status === 403) {
        return {
            title: 'No Firm Access',
            message: 'You signed in, but this account has no access to a firm in Outlaw Practice. ' +
                'Ask your firm\'s administrator to check your access, then choose Retry.',
        };
    }
    return buildLoginError(err);
}

async function ensureQueueDatabaseOpen(firmId: string, userId: string, generation: SessionGeneration): Promise<void> {
    // Check the captured account on both resolution and rejection. A storage
    // failure from an old login must never publish an error into a newer tab
    // generation.
    generation.assertCurrent();
    const result = await idb.openReady(firmId, userId).finally(() => generation.assertCurrent());
    if (result.isErr()) throw result.unwrapErr();
}

class Auth {
    // Delegate role utilities to roles.ts for backward compatibility
    static LEGAL_ROLES = LEGAL_ROLES;
    static ALL_RELATED = ALL_RELATED;
    static getRoles = getRoles;
    static getMyRoles = getMyRoles;
    static hasRole = hasRole;
    static hasRoles = hasRoles;
    static hasRoleId = hasRoleId;
    static hasLegalRole = hasLegalRole;
    static isAdmin = isAdmin;
    static isOwner = isOwner;
    static isUser = isUser;
    static isInGroup = isInGroup;
    static grantRole = grantRole;
    static grantRoleId = grantRoleId;
    static revokeRole = revokeRole;
    static revokeRoleId = revokeRoleId;

    static changeEmail = (userId: uuid, oldEmail: string, newEmail: string, cb: (user: User) => void) => {
        const operation = tabSession.captureGeneration();
        api.changeEmail(userId, oldEmail, newEmail)
            .then((user) => {
                operation.assertCurrent();
                cb(user);
            })
            .catch((err: any) => {
                if (err instanceof SessionChangedError) return;
                try { operation.assertCurrent(); } catch { return; }
                confirmError('The email address was not changed', changeEmailErrorMessage(err));
                cb(null);
            });
    };

    /**
     * Auth0: trigger the password reset email through the Auth0 Authentication API (no auth0-js
     * dependency). Zitadel: point to the reset flow on its sign-in page.
     */
    static changePassword = (options: { email: string; connection: string }): void => {
        if (IDP_CONFIG.provider === 'zitadel') {
            // Zitadel owns the password (OWA-1137); its sign-in page has the reset flow.
            confirmSuccess('Change password', 'Sign out, then choose "Forgot password?" on the sign-in page to set a new password.');
            return;
        }
        fetch(`https://${IDP_CONFIG.domain}/dbconnections/change_password`, {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({
                client_id: IDP_CONFIG.clientId,
                email: options.email,
                connection: options.connection,
            }),
        })
            .then(async (res) => {
                const text = await res.text();
                if (res.ok) confirmSuccess('Success', text);
                else confirmError('Error', text);
            })
            .catch((err) => {
                confirmError('Error', err.message);
            });
    };

    /** Synchronous workspace gate. Credential renewal happens only at outbound requests. */
    static isAuthenticated = (): boolean => tabSession.isLocalReady();

    /**
     * Run the post-login orchestration sequence.
     * Extracted from the old setSession() method. Called by usePostLoginOrchestration hook.
     *
     * This preserves the EXACT timing sequence from the original setSession():
     * 1. Extract app_metadata + user_metadata from ID token claims
     * 2. Dispatch setPortalUser
     * 3. Store firmId, fetch firmInfo
     * 4. Determine which firmId to use (multi-firm logic)
     * 5. Validate firmId and userId exist
     * 6. Portal path: finishSessionSetup() -> polling.start()
     * 7. Non-portal path: verifyLogin() -> subscription check -> finishSessionSetup() -> polling.start()
     * 8. finishSessionSetup() stores identity, dispatches loginResult, opens IDB
     * 9. Finally: clear loading, authenticated support initialization
     */
    static runPostLoginSequence = async (
        app_metadata: any,
        user_metadata: any,
        email: string,
        user_id: string,
        accessToken: string,
        idToken: string,
        _expiresIn: number
    ) => {
        let candidate: LoginCandidate | undefined;
        try {
            candidate = tabSession.beginLogin({accessToken, idToken});
            const zitadel = IDP_CONFIG.provider === 'zitadel';
            if (user_id !== candidate.identity.subject || (!zitadel &&
                (app_metadata?.firmId !== candidate.identity.firmId || app_metadata?.userId !== candidate.identity.userId))) {
                throw new Error('The login response does not match this account.');
            }
            // A Zitadel token names the firm but no Outlaw user (OWA-1137): the server's login check
            // answers with the user row, which names the user before any account store opens.
            let verified: Awaited<ReturnType<typeof api.verifyLogin>> | undefined;
            if (zitadel) {
                verified = await api.verifyLogin(candidate);
                candidate.assertCurrent();
                // Every refusal ends here, before any other account request.
                if (verified.isErr()) {
                    store.dispatch(MiscActions.setConnectionState(false));
                    store.dispatch(MiscActions.loginResult(false, buildZitadelRefusal(verified.unwrapErr()), null, null));
                    return;
                }
                const {status, body} = verified.unwrap();
                if (status < 200 || status >= 300) throw new Error('Sign-in could not be verified. Reconnect and try again.');
                if (body?.firm?.firmId !== candidate.identity.firmId) throw new Error('The verified login belongs to a different firm.');
                // OWA-1280: a Client Portal login (the `client` role) is bound to a contact; the server names it and no user.
                const verifiedUser = candidate.identity.portal ? (body?.user == null ? body?.contact?.contactId : undefined) : body?.user?.userId;
                if (typeof verifiedUser !== 'string' || !verifiedUser) {
                    throw new Error('The server did not name this account\'s user. Sign in again.');
                }
                candidate = tabSession.assignUser(candidate, verifiedUser);
            }
            const {firmId, userId, portal: isPortal} = candidate.identity;
            // Bootstrap carries candidate credentials explicitly; the workspace is not active yet.
            const firms = await api.currentFirmInfo(candidate);
            candidate.assertCurrent();
            // The API returns plain JSON: `id` is a Firm class getter and never on the wire; `firmId` is (OWA-844).
            const bootstrapFirm = firms?.find(value => value.firm?.firmId === firmId)?.firm;
            if (!bootstrapFirm) throw new Error('The authenticated firm could not be verified. Reconnect and try again.');

            store.dispatch(MiscActions.setLoadingMessage('Check login...'));

            if (logging) console.debug(`%c🔑 Auth: Calling api.loginCheck on firm ${firmId}, user ${userId}`, logFormat);

            const finishSessionSetup = async () => {
                await ensureQueueDatabaseOpen(firmId, userId, candidate!);
                candidate!.assertCurrent();
                tabSession.completeLogin(candidate!);
                clearSignedOut();
                store.dispatch(MiscActions.setPortalUser(isPortal));
                store.dispatch(MiscActions.setReadOnly(bootstrapFirm.subscriptionCancelledAt != null));
                locals.setObject(`${locals.USER_FIRM}-${firmId}-${userId}`, bootstrapFirm);
                locals.setObject(`${locals.PORTAL_FIRM}-${firmId}-${userId}`, bootstrapFirm);
                locals.setArray(`${locals.AUTH_FIRMS}-${firmId}-${userId}`, app_metadata?.firms ?? [firmId]);
                setLoggingUser();

                store.dispatch(MiscActions.loginResult(true, '', firmId, userId));
                idb.initQueueCount();
            }

            if (isPortal) {
                await finishSessionSetup();

                polling.addCallback('setSession', fetchFromServer);
                polling.start('setSession').catch(err => console.error('🔑 Auth: Error calling polling start', err));
            }
            else {
                try {
                    const result = verified ?? await api.verifyLogin(candidate);
                    candidate.assertCurrent();

                    if (result.isErr()) {
                        const err = result.unwrapErr();
                        const loginError = buildLoginError(err);
                        store.dispatch(MiscActions.setConnectionState(false));
                        store.dispatch(MiscActions.loginResult(false, loginError, null, null));
                    }
                    else {
                        if (result.unwrap().status < 200 || result.unwrap().status >= 300) {
                            throw new Error('Sign-in could not be verified. Reconnect and try again.');
                        }
                        const {subscription, firm} = result.unwrap().body;
                        if (firm?.firmId !== firmId) throw new Error('The verified login belongs to a different firm.');

                        if (logging) console.debug('%c🔑 Auth: setSession api.loginCheck returned', logFormat, {subscription, firm});

                        if (subscription || firm.subscriptionCancelledAt != null) {
                            if (firm.subscriptionCancelledAt != null) {
                                Alert.add(new Alert(
                                    Alert.WARNING,
                                    firmId,
                                    firm?.addedOn,
                                    `This firm's subscription is cancelled. Reactivate it under Billing in the user menu to make changes.`
                                ));

                                store.dispatch(MiscActions.setReadOnly(true));
                            }


                            await finishSessionSetup();

                            polling.addCallback('setSession', fetchFromServer);
                            polling.start('setSession').catch(err => console.error(`🔑 Auth: Error calling polling start ${err?.message}`, err));
                        }
                        else {
                            const msg = 'No subscription was found for this user. Please contact Support.';
                            console.error(`🔑 Auth: ${msg} firmId=${firmId}, userId=${userId}`);
                            confirmError('No Subscription', msg);
                            store.dispatch(MiscActions.loginResult(false, msg, null, null));
                        }
                    }
                }
                catch (err) {
                    if (err instanceof SessionChangedError) return;
                    try { candidate.assertCurrent(); } catch { return; }
                    // Only thrown for truly unexpected errors (not HTTP status errors)
                    console.error(`🔑 Auth: setSession login error ${err?.message}`, err);
                    const loginError = buildLoginError(err);
                    store.dispatch(MiscActions.setConnectionState(false));
                    store.dispatch(MiscActions.loginResult(false, loginError, null, null));
                }
                finally {
                    try { candidate.assertCurrent(); } catch { return; }
                    store.dispatch(MiscActions.setLoadingMessage(''));
                    store.dispatch(MiscActions.updateLoading(false));
                }
            }
        }
        catch (err) {
            if (err instanceof SessionChangedError) return;
            try { candidate?.assertCurrent(); } catch { return; }
            console.error(`🔑 Auth: setSession login error ${err?.message}`, err);
            const loginError = buildLoginError(err);
            store.dispatch(MiscActions.setConnectionState(false));
            store.dispatch(MiscActions.loginResult(false, loginError, null, null));
        }
        finally {
            try { candidate?.assertCurrent(); } catch { return; }
            store.dispatch(MiscActions.setLoadingMessage(''));
            store.dispatch(MiscActions.updateLoading(false));

            void pWaitFor(() => Auth.isAuthenticated() && !!getCurrentUser(), {signal: candidate?.signal ?? globalAbortSignal, timeout: 20000})
                .then(() => initializeSupport()).catch(() => { /* Offline or cancelled login: support can open later. */ });
        }
    };

    /** Restore an established matching workspace without waiting for SDK/network startup. */
    static restoreTabSession = async (): Promise<boolean> => {
        if (!tabSession.isLocalReady()) return false;
        const operation = tabSession.capture();
        const {identity} = operation;
        await ensureQueueDatabaseOpen(identity.firmId, identity.userId, operation);
        operation.assertCurrent();
        store.dispatch(MiscActions.setPortalUser(identity.portal));
        store.dispatch(MiscActions.loginResult(true, '', identity.firmId, identity.userId));
        idb.initQueueCount();
        store.dispatch(MiscActions.setLoadingMessage(''));
        store.dispatch(MiscActions.updateLoading(false));
        return true;
    };

    /**
     * Perform logout cleanup (stop polling, disconnect socket, clear storage).
     * The actual Auth0 logout redirect is handled by the Auth0 SDK in useLogout.
     */
    static logoutBlockReason = (discardUnsaved = false): string | undefined => {
        const status = store.getState().savedStatus;
        const allSaved = status.length === 0;
        if (!persistenceGate.isSafe()) {
            return 'Some work has not finished saving on this device. Keep this tab open, resolve any save error, and try again.';
        }
        if (!allSaved && !discardUnsaved) {
            return 'Save or cancel your unfinished edits before logging out. This tab is staying open to preserve your work.';
        }
        return undefined;
    };

    static canLogout = (discardUnsaved = false): boolean => {
        const reason = Auth.logoutBlockReason(discardUnsaved);
        if (reason) {
            confirmError('Sign out paused', reason);
            return false;
        }
        return true;
    };

    static logoutCleanup = async (isPortal?: boolean, discardUnsaved = false): Promise<SessionGeneration | false> => {
        if (logging) console.debug(`%c🔑 Auth: Logout (portal?: ${isPortal}`, logFormat);
        if (!Auth.canLogout(discardUnsaved)) return false;
        const operation = tabSession.captureGeneration();
        try {
            await stopRecording(true);
            operation.assertCurrent();
        }
        catch {
            confirmError('Sign out paused', 'The diagnostic recording has not finished saving and remains only in this open tab. Closing or reloading this tab can lose it. Keep the tab open and retry after reconnecting or signing in again. If saving is still refused, contact Support.');
            return false;
        }
        if (!Auth.canLogout(discardUnsaved)) return false;

        resetSupport();
        polling.stop('logout');

        socket?.disconnect();

        tabSession.signOut();
        const signedOut = tabSession.captureGeneration();
        idb.close();

        return signedOut;
    };
}

export default Auth;
